import type React from 'react'
import type {
  MultipleChoiceAggregate,
  NpsAggregate,
  QuantitativeAggregate,
  RankingAggregate,
  RatingAggregate,
  SingleChoiceAggregate,
} from '@knownow/domain'

export interface PlanSummaryChartProps {
  readonly aggregate: QuantitativeAggregate
}

/**
 * Dispatcher for the widget-typed aggregate renderers below. Each sub-view
 * uses flexbox + design-system color tokens — no chart library needed for
 * these shapes, which keeps the bundle lean and the visuals consistent
 * with the rest of the app.
 */
export function PlanSummaryChart({
  aggregate,
}: PlanSummaryChartProps): React.ReactElement {
  switch (aggregate.widgetType) {
    case 'rating':
      return <RatingChart aggregate={aggregate} />
    case 'nps':
      return <NpsChart aggregate={aggregate} />
    case 'single_choice':
      return <SingleChoiceChart aggregate={aggregate} />
    case 'multiple_choice':
      return <MultipleChoiceChart aggregate={aggregate} />
    case 'ranking':
      return <RankingChart aggregate={aggregate} />
  }
}

function CountBar({
  label,
  count,
  total,
  testId,
}: {
  label: string
  count: number
  total: number
  testId?: string
}): React.ReactElement {
  const pct = total === 0 ? 0 : Math.round((100 * count) / total)
  return (
    <div
      className="grid grid-cols-[5rem_1fr_3rem] items-center gap-2"
      data-testid={testId}
    >
      <span className="text-xs text-neutral-600 truncate">{label}</span>
      <div
        role="progressbar"
        aria-valuenow={count}
        aria-valuemin={0}
        aria-valuemax={total}
        className="h-2 rounded-full bg-neutral-100 overflow-hidden"
      >
        <div
          className="h-full bg-brand-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-neutral-500 text-right tabular-nums">
        {count} · {pct}%
      </span>
    </div>
  )
}

function RatingChart({
  aggregate,
}: {
  aggregate: RatingAggregate
}): React.ReactElement {
  return (
    <div className="space-y-2" data-testid="rating-chart">
      <dl className="flex gap-6 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wider text-neutral-500">
            Mean
          </dt>
          <dd className="font-serif text-2xl text-neutral-900">
            {aggregate.mean.toFixed(2)}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-neutral-500">
            Median
          </dt>
          <dd className="font-serif text-2xl text-neutral-900">
            {aggregate.median}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-neutral-500">
            Responses
          </dt>
          <dd className="font-serif text-2xl text-neutral-900">{aggregate.n}</dd>
        </div>
      </dl>
      <div className="space-y-1">
        {aggregate.distribution.map((row) => (
          <CountBar
            key={row.value}
            label={String(row.value)}
            count={row.count}
            total={aggregate.n}
          />
        ))}
      </div>
    </div>
  )
}

function NpsChart({
  aggregate,
}: {
  aggregate: NpsAggregate
}): React.ReactElement {
  return (
    <div className="space-y-3" data-testid="nps-chart">
      <div className="flex items-baseline gap-4">
        <span className="font-serif text-4xl text-neutral-900">
          {aggregate.score}
        </span>
        <span className="text-sm text-neutral-500">NPS score</span>
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg bg-success-light px-3 py-2">
          <dt className="text-xs text-success-dark">Promoters</dt>
          <dd className="font-serif text-xl text-success-dark">
            {aggregate.promoters}
          </dd>
        </div>
        <div className="rounded-lg bg-warning-light px-3 py-2">
          <dt className="text-xs text-warning-dark">Passives</dt>
          <dd className="font-serif text-xl text-warning-dark">
            {aggregate.passives}
          </dd>
        </div>
        <div className="rounded-lg bg-error-light px-3 py-2">
          <dt className="text-xs text-error-dark">Detractors</dt>
          <dd className="font-serif text-xl text-error-dark">
            {aggregate.detractors}
          </dd>
        </div>
      </div>
      <div className="space-y-1">
        {aggregate.distribution.map((row) => (
          <CountBar
            key={row.value}
            label={String(row.value)}
            count={row.count}
            total={aggregate.n}
          />
        ))}
      </div>
    </div>
  )
}

function SingleChoiceChart({
  aggregate,
}: {
  aggregate: SingleChoiceAggregate
}): React.ReactElement {
  return (
    <div className="space-y-1" data-testid="single-choice-chart">
      {aggregate.counts.map((row) => (
        <CountBar
          key={row.option}
          label={row.option}
          count={row.count}
          total={aggregate.n}
        />
      ))}
    </div>
  )
}

function MultipleChoiceChart({
  aggregate,
}: {
  aggregate: MultipleChoiceAggregate
}): React.ReactElement {
  return (
    <div className="space-y-1" data-testid="multiple-choice-chart">
      {aggregate.counts.map((row) => (
        <CountBar
          key={row.option}
          label={row.option}
          count={row.count}
          total={aggregate.n}
        />
      ))}
    </div>
  )
}

function RankingChart({
  aggregate,
}: {
  aggregate: RankingAggregate
}): React.ReactElement {
  const sorted = [...aggregate.meanRanks].sort(
    (a, b) => a.meanRank - b.meanRank,
  )
  return (
    <ol className="space-y-1" data-testid="ranking-chart">
      {sorted.map((row, i) => (
        <li
          key={row.item}
          className="flex items-center gap-2 text-sm text-neutral-800"
        >
          <span className="font-serif text-neutral-500 w-6 text-right">
            {i + 1}.
          </span>
          <span className="flex-1">{row.item}</span>
          <span className="text-xs text-neutral-500 tabular-nums">
            mean rank {row.meanRank.toFixed(2)}
          </span>
        </li>
      ))}
    </ol>
  )
}
